
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useChartConfig } from '../Charts/utils/ChartConfigContext';
import { ChartConfig } from '../Charts/models/interfaces';
import { sanitizeForLog } from 'src/utils/security';

const useAsyncChartConfig = (
  chartType: string | undefined,
  viewType: string,
  grid: string,
  dataLength?: number | null,
  numberOfBars?: number | null,
  enabled: boolean = true
) => {
  const { getChartConfig } = useChartConfig();
  const [config, setConfig] = useState<ChartConfig["config"] | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const configParams = useMemo(() => ({
    chartType,
    viewType,
    grid,
    dataLength: dataLength || undefined,
    numberOfBars: numberOfBars || undefined,
    enabled
  }), [chartType, viewType, grid, dataLength, numberOfBars, enabled]);

  const loadConfig = useCallback(async () => {
    if (!configParams.enabled || !configParams.chartType) {
      setConfig(undefined);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getChartConfig(
        configParams.chartType,
        configParams.viewType,
        configParams.grid,
        configParams.dataLength,
        configParams.numberOfBars
      );
      setConfig(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load config';
      setError(errorMessage);
      setConfig(undefined);
      console.error(`Failed to load config for ${sanitizeForLog(configParams.chartType)}:`, err);
    } finally {
      setLoading(false);
    }
  }, [getChartConfig, configParams]);

  // Simplified useEffect with stable loadConfig dependency
  useEffect(() => {
    loadConfig();
  }, [loadConfig]); // Only depends on stable loadConfig callback


  return { config, loading, error };
};

export default useAsyncChartConfig;